import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCalendars } from './hooks';
import { CalendarMetricsCards, ConnectedCalendars } from './components';
import { EventList } from './components/CalendarEvent';
import { PageLoader, FullWidthContainer, Modal, Input } from '../../components';
import { useMinimumLoader } from '../../hooks/useMinimumLoader';
import { Button } from '../../components/Button';
import { CalendarProvider } from './types';
import { calendarIntegrationService } from './services/calendarIntegration';
import { oauthService } from './services/oauth';
import { CalDavConnectionForm } from './components/CalDavConnectionForm';

const Calendars: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { metrics, connectedCalendars, loading, error, disconnectCalendar, refetch } =
    useCalendars();
  const showLoader = useMinimumLoader(loading, { minimumDuration: 500 });
  const [activeTab, setActiveTab] = useState<'overview' | 'events'>('overview');
  const [connectingProvider, setConnectingProvider] = useState<CalendarProvider | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [caldavModal, setCaldavModal] = useState<{
    isOpen: boolean;
    provider: 'apple' | 'caldav';
  }>({
    isOpen: false,
    provider: 'apple',
  });

  const [editModal, setEditModal] = useState<{ isOpen: boolean; id: string | null; name: string }>({
    isOpen: false,
    id: null,
    name: '',
  });

  const handleEditCalendar = (id: string) => {
    const cal = connectedCalendars.find(c => c.id === id);
    setEditModal({ isOpen: true, id, name: cal?.name || '' });
  };

  const saveCalendarName = async () => {
    if (!editModal.id) return;
    await calendarIntegrationService.update(editModal.id, { name: editModal.name });
    setEditModal({ isOpen: false, id: null, name: '' });
    await refetch();
  };

  const handleDisconnectCalendar = async (id: string) => {
    await disconnectCalendar(id);
    await refetch();
  };

  const handleRefreshAll = async () => {
    try {
      setIsRefreshing(true);
      const start = Date.now();
      await refetch();
      const elapsed = Date.now() - start;
      const minDuration = 800; // ms
      if (elapsed < minDuration) {
        await new Promise(resolve => setTimeout(resolve, minDuration - elapsed));
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleConnectCalendar = async (provider: CalendarProvider) => {
    try {
      setConnectingProvider(provider);
      setIsConnecting(true);
      if (provider === 'apple' || provider === 'caldav') {
        setCaldavModal({
          isOpen: true,
          provider: provider === 'apple' ? 'apple' : 'caldav',
        });
        setIsConnecting(false);
        setConnectingProvider(null);
        return;
      }
      const redirectUri = `${window.location.origin}/calendars/oauth/callback`;
      sessionStorage.setItem('oauth_provider', provider);
      const authUrl = await oauthService.start(provider, redirectUri);
      window.location.href = authUrl;
    } catch (err) {
      console.error('Failed to start OAuth flow:', err);
      setConnectingProvider(null);
      setIsConnecting(false);
    }
  };

  const handleCalDavSuccess = async () => {
    await refetch();
    navigate('/calendars/connect/success');
  };

  if (showLoader) {
    return <PageLoader show={true} />;
  }

  if (error) {
    return (
      <FullWidthContainer>
        <div className="space-y-6">
          <div className="text-center py-8">
            <p className="text-red-500 text-lg font-semibold">Error loading calendars</p>
            <p className="text-sm text-gray-500 mt-2">{error}</p>
          </div>
        </div>
        {/* Edit Calendar Name Modal */}
        <Modal
          isOpen={editModal.isOpen}
          onClose={() => setEditModal({ isOpen: false, id: null, name: '' })}
          title=""
          size="sm"
          showCloseButton={false}
        >
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('calendars.editCalendar')}
            </h3>
            <Input
              value={editModal.name}
              onChange={e => setEditModal(prev => ({ ...prev, name: e.target.value }))}
              placeholder={t('calendars.calendarName')}
            />
            <div className="flex gap-3 justify-end">
              <Button
                variant="secondary"
                onClick={() => setEditModal({ isOpen: false, id: null, name: '' })}
              >
                {t('common.cancel')}
              </Button>
              <Button onClick={saveCalendarName} disabled={!editModal.name.trim()}>
                {t('common.save')}
              </Button>
            </div>
          </div>
        </Modal>
      </FullWidthContainer>
    );
  }

  return (
    <FullWidthContainer>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {t('calendars.title')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">{t('calendars.subtitle')}</p>
          </div>
        </div>

        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('calendars.overview')}
            </button>
            <button
              onClick={() => setActiveTab('events')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'events'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('calendars.events')}
            </button>
          </nav>
        </div>

        {activeTab === 'overview' && (
          <div className="space-y-6">
            <CalendarMetricsCards metrics={metrics} />

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('calendars.connectCalendar')}
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRefreshAll}
                  disabled={isRefreshing}
                  className="p-1 h-8 w-8"
                  title={t('common.refresh')}
                >
                  <span
                    className="material-icons text-lg"
                    style={{
                      animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                    }}
                  >
                    sync
                  </span>
                </Button>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => handleConnectCalendar('google')}
                  variant="gradient-multi"
                  size="md"
                  disabled={isConnecting}
                  loading={isConnecting && connectingProvider === 'google'}
                >
                  {t('calendars.connectGoogle')}
                </Button>
                <Button
                  onClick={() => handleConnectCalendar('outlook')}
                  variant="gradient-multi"
                  size="md"
                  disabled={isConnecting}
                  loading={isConnecting && connectingProvider === 'outlook'}
                >
                  {t('calendars.connectOutlook')}
                </Button>
                <Button
                  onClick={() => handleConnectCalendar('apple')}
                  variant="gradient-multi"
                  size="md"
                  disabled={isConnecting}
                  loading={isConnecting && connectingProvider === 'apple'}
                >
                  {t('calendars.connectApple')}
                </Button>
                <Button
                  onClick={() => handleConnectCalendar('caldav')}
                  variant="gradient-multi"
                  size="md"
                  disabled={isConnecting}
                  loading={isConnecting && connectingProvider === 'caldav'}
                >
                  {t('calendars.connectCalDAV')}
                </Button>
              </div>
            </div>

            <ConnectedCalendars
              calendars={connectedCalendars}
              onEdit={handleEditCalendar}
              onDisconnect={handleDisconnectCalendar}
            />

            <CalDavConnectionForm
              isOpen={caldavModal.isOpen}
              onClose={() => setCaldavModal({ isOpen: false, provider: 'apple' })}
              onSuccess={handleCalDavSuccess}
              provider={caldavModal.provider}
            />
          </div>
        )}

        {activeTab === 'events' && <EventList />}
      </div>
    </FullWidthContainer>
  );
};

export { Calendars };
